/**
 * Document head, built once and applied two ways.
 *
 * `renderHeadToString` runs at build time (scripts/prerender.ts) so the tags
 * are in the HTML a scraper downloads. `applyHead` runs in the browser so a
 * client-side navigation — or `npm run dev`, which has no prerender pass —
 * ends up with the identical set.
 *
 * Both read the same RouteMeta, which is the only way to keep the shared and
 * the rendered versions of a page in agreement.
 */

import {
  OG_IMAGE,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  SITE_NAME,
  type RouteMeta,
} from './seo';
import { SITE_URL } from './site';

export interface HeadTag {
  tag: 'meta' | 'link';
  attrs: Record<string, string>;
}

export interface HeadData {
  title: string;
  tags: HeadTag[];
}

/** Absolute URL for a site-relative path. OG and canonical both require one. */
function absolute(path: string): string {
  return new URL(path, SITE_URL).href;
}

export function buildHead(meta: RouteMeta): HeadData {
  const url = absolute(meta.path);
  const image = absolute(OG_IMAGE);

  const tags: HeadTag[] = [
    { tag: 'meta', attrs: { name: 'description', content: meta.description } },
    { tag: 'link', attrs: { rel: 'canonical', href: url } },

    { tag: 'meta', attrs: { property: 'og:type', content: 'website' } },
    { tag: 'meta', attrs: { property: 'og:site_name', content: SITE_NAME } },
    { tag: 'meta', attrs: { property: 'og:locale', content: 'en_US' } },
    { tag: 'meta', attrs: { property: 'og:url', content: url } },
    { tag: 'meta', attrs: { property: 'og:title', content: meta.ogTitle } },
    {
      tag: 'meta',
      attrs: { property: 'og:description', content: meta.ogDescription },
    },
    { tag: 'meta', attrs: { property: 'og:image', content: image } },
    {
      tag: 'meta',
      attrs: { property: 'og:image:width', content: OG_IMAGE_WIDTH },
    },
    {
      tag: 'meta',
      attrs: { property: 'og:image:height', content: OG_IMAGE_HEIGHT },
    },

    {
      tag: 'meta',
      attrs: { name: 'twitter:card', content: 'summary_large_image' },
    },
    { tag: 'meta', attrs: { name: 'twitter:title', content: meta.ogTitle } },
    {
      tag: 'meta',
      attrs: { name: 'twitter:description', content: meta.ogDescription },
    },
    { tag: 'meta', attrs: { name: 'twitter:image', content: image } },
  ];

  if (meta.themeColor) {
    tags.push({
      tag: 'meta',
      attrs: { name: 'theme-color', content: meta.themeColor },
    });
  }

  return { title: meta.title, tags };
}

/**
 * The selector that identifies a tag as "the same tag" across renders.
 *
 * Upserting by this rather than appending is what stops a client-side
 * navigation from stacking a second og:title on top of the prerendered one —
 * scrapers and browsers both take the first, so appending would silently pin
 * every page to the metadata of whichever one loaded first.
 */
function selectorFor(tag: HeadTag): string {
  if (tag.tag === 'link') return `link[rel="${tag.attrs.rel}"]`;
  if (tag.attrs.property) return `meta[property="${tag.attrs.property}"]`;
  return `meta[name="${tag.attrs.name}"]`;
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Build-time: the string injected into <head> by the prerender script. */
export function renderHeadToString(head: HeadData): string {
  const lines = [`<title>${escapeText(head.title)}</title>`];

  for (const tag of head.tags) {
    const attrs = Object.entries(tag.attrs)
      .map(([key, value]) => `${key}="${escapeAttribute(value)}"`)
      .join(' ');
    lines.push(`<${tag.tag} ${attrs} />`);
  }

  return lines.join('\n    ');
}

/** Runtime: reconcile the live document against `head`. */
export function applyHead(head: HeadData): void {
  document.title = head.title;

  for (const tag of head.tags) {
    const selector = selectorFor(tag);
    let element = document.head.querySelector(selector);

    if (!element) {
      element = document.createElement(tag.tag);
      document.head.appendChild(element);
    }

    for (const [key, value] of Object.entries(tag.attrs)) {
      element.setAttribute(key, value);
    }
  }
}

/**
 * The <html>/<body>/viewport half of the shell.
 *
 * Prerendered pages already ship with these baked in, so this is a no-op on
 * first paint in production. It earns its keep in dev and on the client-side
 * navigations that cross between the site and the arcade shells.
 */
export function applyDocumentShell(meta: RouteMeta): void {
  document.documentElement.className = meta.htmlClass;
  document.body.className = meta.bodyClass;

  const viewport = document.head.querySelector('meta[name="viewport"]');
  if (viewport) viewport.setAttribute('content', meta.viewport);
}
